import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Snackbar, Alert } from '@mui/material';
import { store } from './store';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store';
import { hideSnackbar } from './store/slices/uiSlice';
import { getProfile } from './store/slices/authSlice';

// Pages
import Login from './pages/Login';
import ItemMaster from './pages/ItemMaster';
import LocalItemMaster from './pages/LocalItemMaster';
import MainLayout from './components/layout/MainLayout';
import ModuleRoute from './components/routing/ModuleRoute';
import MODULES from './config/moduleRegistry';
import { getFirstAccessibleModulePath } from './config/moduleRegistry';
import ResultDialog from './components/common/ResultDialog';
import PRDetail from './pages/PRDetail';
import PODetail from './pages/PODetail';
import PaymentRequestList from './pages/PaymentRequestList';
import PaymentRequestCreate from './pages/PaymentRequestCreate';
import PaymentRequestDetail from './pages/PaymentRequestDetail';
import PaymentRequestWorkflow from './pages/PaymentRequestWorkflow';
import ApprovalLink from './pages/ApprovalLink';
import POEmailAction from './pages/POEmailAction';
import SapPoApprovalAction from './pages/SapPoApprovalAction';
import POBulkApproval from './pages/POBulkApproval';
import CompanySelectionDialog from './components/common/CompanySelectionDialog';
import { setSignedInAccess } from './utils/userAccess';


// Base theme — keeps the existing Fiori-inspired look (Belize navy AppBar,
// dense controls). Responsive overrides are applied below via
// `responsiveFontSizes` and per-component breakpoint rules so the desktop
// experience is unchanged while small viewports get readable scaling.
let theme = createTheme({
  palette: {
    primary: {
      main: '#F1C232',
      contrastText: '#1A1A1A',
    },
    secondary: {
      main: '#F7D14A',
      contrastText: '#1A1A1A',
    },
    background: {
      default: '#f7f7f7',
      paper: '#ffffff',
    },
    text: {
      primary: '#32363a',
      secondary: '#6a6d70',
    },
    info: { main: '#F1C232' },
  },
  typography: {
    fontFamily: 'Segoe UI, Roboto, Arial, sans-serif',
    // Slightly larger root for readability on small screens; MUI scales other
    // variants from this. Desktop visuals are preserved by leaving variant
    // sizes at their defaults below.
    htmlFontSize: 16,
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
        },
        // PRINT-SAFETY: leave printable surfaces alone; the Payment Certificate
        // and backend PDFs use isolated CSS not affected by these rules.
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          // Belize Shell header color
          backgroundColor: '#354a5f',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid #cfd8dc',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        color: 'primary',
        size: 'small',
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme: t }) => ({
          minHeight: 30,
          padding: '4px 12px',
          borderRadius: 3,
          textTransform: 'none',
          fontSize: 13,
          // Bump tap targets on touch viewports to the 44px WCAG recommendation.
          [t.breakpoints.down('sm')]: {
            minHeight: 40,
            padding: '8px 14px',
            fontSize: 14,
          },
        }),
        containedPrimary: {
          background: 'linear-gradient(180deg, #F9E27A 0%, #F7D14A 50%, #E8B640 100%)',
          color: '#1A1A1A',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
        },
        outlinedPrimary: {
          borderColor: '#E8B640',
          color: '#C58B00'
        }
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme: t }) => ({
          [t.breakpoints.down('sm')]: {
            padding: 10,
          },
        }),
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          borderRadius: 3,
          '& .MuiInputBase-input': {
            padding: '6px 10px',
            fontSize: 13,
          },
          // Mobile: larger input padding + 16px font to suppress iOS zoom-on-focus.
          [t.breakpoints.down('sm')]: {
            '& .MuiInputBase-input': {
              padding: '10px 12px',
              fontSize: 16,
            },
          },
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          fontSize: 12,
          [t.breakpoints.down('sm')]: {
            fontSize: 13,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          padding: '6px 10px',
          fontSize: 13,
          [t.breakpoints.down('sm')]: {
            padding: '8px 8px',
            fontSize: 13,
          },
        }),
        head: {
          fontWeight: 600,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme: t }) => ({
          minHeight: 32,
          paddingTop: 4,
          paddingBottom: 4,
          [t.breakpoints.down('sm')]: {
            minHeight: 44,
            paddingTop: 8,
            paddingBottom: 8,
          },
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme: t }) => ({
          borderRadius: 6,
          border: '1px solid #cfd8dc',
          // Mobile: dialogs that didn't opt into ResponsiveDialog still get
          // sensible mobile treatment — fill the viewport instead of floating.
          [t.breakpoints.down('sm')]: {
            margin: 0,
            maxHeight: '100%',
            borderRadius: 0,
            border: 'none',
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 24,
          fontSize: 12,
        },
      },
    },
  },
});

// Apply responsive font scaling (h1–h6, subtitle, body, caption all clamp down
// gracefully on narrow viewports). Body text stays readable on 360px without
// affecting the dense desktop layout.
theme = responsiveFontSizes(theme, { factor: 2 });

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const loc = (typeof window !== 'undefined') ? window.location : { pathname: '/', search: '' } as any;
  const ret = encodeURIComponent(`${loc.pathname}${loc.search}`);
  return isAuthenticated ? <>{children}</> : <Navigate to={`/login?return=${ret}`} />;
};

// Root redirect.
//
// The legacy /m/* mobile bundle was retired after the responsive rollout —
// the desktop pages work down to 360px so every user lands on the same
// responsive entry.
const MobileRedirect: React.FC = () => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const allowedModules: string[] = ((user as any)?.allowedmodules ?? []) as string[];
  const fallbackPath = getFirstAccessibleModulePath(allowedModules) || '/login';
  return <Navigate to={fallbackPath} replace />;
};

// App Content (needs to be inside Provider to use Redux)
const AppContent: React.FC = () => {
  const dispatch = useDispatch();
  const { snackbar } = useSelector((state: RootState) => state.ui);
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const [showCompanyDialog, setShowCompanyDialog] = React.useState(false);

  // Ensure user profile is loaded when app mounts or after refresh
  React.useEffect(() => {
    if (isAuthenticated && !user) {
      dispatch(getProfile() as any);
    }
  }, [isAuthenticated, user, dispatch]);

  // Refresh the profile when the tab regains focus so access changes made by
  // admins (module grants, roles, companies) reach long-lived sessions
  // without requiring a re-login. Throttled to once per minute.
  const lastProfileRefreshRef = React.useRef(0);
  React.useEffect(() => {
    if (!isAuthenticated) return;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastProfileRefreshRef.current < 60_000) return;
      lastProfileRefreshRef.current = now;
      dispatch(getProfile({ silent: true }) as any);
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [isAuthenticated, dispatch]);

  // The signed-in user's projects, divisions and departments per company, for the pickers (utils/userAccess).
  React.useEffect(() => {
    setSignedInAccess(user);
  }, [user]);

  // Check if company selection is needed
  React.useEffect(() => {
    if (!isAuthenticated) {
      setShowCompanyDialog(false);
      return;
    }

    const selectedCompanyId = localStorage.getItem('selectedCompanyId');
    if (!selectedCompanyId) {
      // No company selected, show dialog
      setShowCompanyDialog(true);
    } else {
      setShowCompanyDialog(false);
    }
  }, [isAuthenticated]);

  const handleCompanySelected = (companyId: number, companyName: string, databaseName: string) => {
    try {
      localStorage.setItem('selectedCompanyId', String(companyId));
      localStorage.setItem('selectedCompanyName', companyName);
      localStorage.setItem('selectedDatabaseName', databaseName);
      setShowCompanyDialog(false);

      // Dispatch custom event for other components to listen
      window.dispatchEvent(new CustomEvent('company-changed', {
        detail: { companyId, companyName, databaseName }
      }));

      // Reload profile to get updated permissions
      dispatch(getProfile() as any);
    } catch (error) {
      console.error('Failed to save company selection:', error);
    }
  };

  const handleCloseSnackbar = () => {
    dispatch(hideSnackbar());
  };

  return (
    <>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/item-master" element={<ItemMaster />} />
          <Route path="/local-item-master" element={<LocalItemMaster />} />
          <Route path="/pr/:id" element={<ProtectedRoute><MainLayout><PRDetail /></MainLayout></ProtectedRoute>} />
          <Route path="/po/:id" element={<ProtectedRoute><MainLayout><PODetail /></MainLayout></ProtectedRoute>} />
          <Route path="/payment-requests" element={<ProtectedRoute><MainLayout><PaymentRequestList /></MainLayout></ProtectedRoute>} />
          <Route path="/payment-requests/new" element={<ProtectedRoute><MainLayout><PaymentRequestCreate /></MainLayout></ProtectedRoute>} />
          <Route path="/payment-requests/:id/edit" element={<ProtectedRoute><MainLayout><PaymentRequestCreate /></MainLayout></ProtectedRoute>} />
          <Route path="/payment-requests/:id" element={<ProtectedRoute><MainLayout><PaymentRequestDetail /></MainLayout></ProtectedRoute>} />
          <Route path="/admin/payment-request-workflow" element={<ProtectedRoute><MainLayout><PaymentRequestWorkflow /></MainLayout></ProtectedRoute>} />
          <Route path="/approval/:token" element={<MainLayout><ApprovalLink /></MainLayout>} />
          <Route path="/po-approval/:token" element={<POEmailAction />} />
          <Route path="/sap-po-approval/:token" element={<SapPoApprovalAction />} />
          <Route path="/po-bulk-approval/:token" element={<POBulkApproval />} />

          {/* Legacy /m/* mobile bundle was retired in the responsive rollout.
              Direct hits (bookmarks, old emails) fall through to the catch-all
              "/" → MobileRedirect, which sends the user to their responsive
              landing page. */}
          <Route path="/m/*" element={<Navigate to="/" replace />} />

          {MODULES.map((item) => {
            const content = (
              <MainLayout>
                <item.component />
              </MainLayout>
            );
            const guarded = (item.moduleKey || (item.roles && item.roles.length > 0)) ? (
              <ModuleRoute moduleKey={item.moduleKey || ''} roles={item.roles}>
                {content}
              </ModuleRoute>
            ) : (
              content
            );
            return (
              <Route
                key={item.key}
                path={item.path}
                element={<ProtectedRoute>{guarded}</ProtectedRoute>}
              />
            );
          })}
          <Route path="/" element={<MobileRedirect />} />
        </Routes>
        <ResultDialog />
      </Router>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <CompanySelectionDialog
        open={showCompanyDialog}
        onClose={() => setShowCompanyDialog(false)}
        onCompanySelected={handleCompanySelected}
      />
    </>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
